import { Box } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { getSection } from "../../screens/root/api/rootScreenApi";
import { Release } from "../../types";
import { YearlyBundleReleaseCard } from "./YearlyBundleReleaseCard";
import { useState, useEffect } from "react";

interface YearlyBundleReleasesGridProps {
  sectionKeyProd: string;
  sectionKeyDev: string;
  queryKeyName: string;
}

export function YearlyBundleReleasesGrid({
  sectionKeyProd,
  sectionKeyDev,
  queryKeyName,
}: YearlyBundleReleasesGridProps) {
  // Custom breakpoint logic for better grid layout
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Define breakpoints for grid layout
  const getGridConfig = () => {
    if (windowWidth < 744) {
      // Mobile: 2 columns, 5 rows
      return {
        columns: "1fr 1fr",
        rows: "repeat(5, auto)",
        justifyItems: "center",
      };
    } else if (windowWidth < 1000) {
      // Medium: 4 columns, 3 rows (with last row having 2 items)
      return {
        columns: "repeat(4, 1fr)",
        rows: "repeat(3, auto)",
        justifyItems: "center",
      };
    } else {
      // Large: 5 columns, 2 rows
      return {
        columns: "repeat(5, 1fr)",
        rows: "repeat(2, auto)",
        justifyItems: "stretch",
      };
    }
  };

  const gridConfig = getGridConfig();

  const sectionKey = import.meta.env.PROD ? sectionKeyProd : sectionKeyDev;

  const { data } = useQuery({
    queryKey: [queryKeyName, sectionKey],
    queryFn: () => {
      return getSection(sectionKey);
    },
  });

  // Get releases from the section data, fallback to empty array if no data
  const releases = data?.data.payload.results.releases || [];

  // Take first 10 releases for the grid
  const displayReleases = releases.slice(0, 10);

  return (
    <Box
      display="grid"
      gridTemplateColumns={gridConfig.columns}
      gridTemplateRows={gridConfig.rows}
      gap="16px"
      width="100%"
      maxWidth="1000px"
      justifyItems={gridConfig.justifyItems}
    >
      {displayReleases.map((release: Release, index: number) => (
        <YearlyBundleReleaseCard
          key={release.id || index}
          content={release}
          layout={{
            display: {
              include_cover_image: true,
              include_title: true,
            },
            style: {
              context: "releases",
              mode: "grid",
              size: "l",
              rows: 0,
              corners_top: "left",
              corners_bottom: "left",
              section_is_inset: false,
            },
          }}
        />
      ))}
    </Box>
  );
}
